import {
  HHAlert,
  HHStack,
  HHToggleButton,
  HHToggleButtonGroup,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useCallback, useState } from 'react';
import { useGetDiscountsPaginated } from '../../hooks/discount-hooks';
import DiscountComponent from '../discounts/discount-component';
import RevenueShareComponent from '../revenue-shares/revenue-share-component';

enum DiscountView {
  Discounts = 'discounts',
  RevenueShares = 'revenue-shares',
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 5;

export interface ClientDiscountsListProps {
  clientId: number;
  partnershipIds?: number[];
  disableAddNew?: boolean;
}

const ClientDiscountsList = ({
  clientId,
  partnershipIds,
  disableAddNew = false,
}: ClientDiscountsListProps): JSX.Element | null => {
  const [view, setView] = useState<DiscountView>(DiscountView.Discounts);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // ACCOUNTS-AND-PLAN-MIGRATION: clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  const {
    data: discountsData,
    isLoading,
    hasError,
    total,
    usageLoading,
    usageError,
  } = useGetDiscountsPaginated({
    clientId,
    partnershipIds,
    page,
    size: pageSize,
    skip: view !== DiscountView.Discounts,
  });

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(DEFAULT_PAGE);
  }, []);

  const handleViewChange = useCallback(
    (_e: React.MouseEvent<HTMLElement>, newView: DiscountView | null): void => {
      if (newView) {
        setView(newView);
      }
    },
    [],
  );

  if (view === DiscountView.Discounts && !isLoading && hasError) {
    return (
      <HHAlert
        hhVariant="outlined"
        severity="error"
        children={
          <HHTypography hhVariant="body">
            There was an error loading client discount list
          </HHTypography>
        }
      ></HHAlert>
    );
  }

  return (
    <HHStack direction="column" spacing={4}>
      <HHStack direction="row" alignItems="center" spacing={2}>
        <HHToggleButtonGroup
          exclusive
          value={view}
          onChange={handleViewChange}
          size="small"
          aria-label="discounts or revenue shares toggle"
          sx={{ height: '40px' }}
        >
          <HHToggleButton
            hhVariant="variant-bypass"
            value={DiscountView.Discounts}
            aria-label="discounts view"
            data-testid="discounts-toggle"
            sx={{ borderRadius: '4px 0 0 4px', textTransform: 'none' }}
          >
            Discounts
          </HHToggleButton>
          <HHToggleButton
            hhVariant="variant-bypass"
            value={DiscountView.RevenueShares}
            aria-label="revenue shares view"
            data-testid="revenue-shares-toggle"
            sx={{ borderRadius: '0 4px 4px 0', textTransform: 'none' }}
          >
            Revenue Shares
          </HHToggleButton>
        </HHToggleButtonGroup>
      </HHStack>

      {view === DiscountView.Discounts ? (
        <DiscountComponent
          loading={isLoading}
          discountsData={discountsData}
          partnershipIds={partnershipIds}
          clientId={clientId}
          usageStatus={{ loading: usageLoading, error: usageError }}
          disableAddNew={disableAddNew}
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      ) : (
        <RevenueShareComponent
          clientId={clientId}
          partnershipIds={partnershipIds}
        />
      )}
    </HHStack>
  );
};

export default ClientDiscountsList;
